import type { ValidationError } from '@angular/forms/signals';
import { validate } from '@angular/forms/signals';
import { CountryIds } from '@indutrust/shared-model';
import { isValidCountryId } from '../../country';
import type { ObjectSchemaPathTree } from '../object-schema-path-tree.type';

/**
 * Phone number regex patterns per country.
 *
 * Supports numbers with or without leading zero, dial codes, and specific formats.
 * Source: https://github.com/ariankoochak/regex-patterns-of-all-countries
 */
const COUNTRIES_PHONE_PATTERNS: Record<number, RegExp> = {
  [CountryIds.UAE]: /^(((\+|00)?971)|0)?5[024568]\d{7}$/,
  [CountryIds.KSA]: /^(((\+|00)?966)|0)?5\d{8}$/,
  [CountryIds.Qatar]: /^(((\+|00)?974)|0)?[3567]\d{7}$/,
  [CountryIds.Oman]: /^(((\+|00)?968)|0)?(9[1-9])\d{6}$/,
  [CountryIds.Bahrain]: /^(((\+|00)?973)|0)?([36])\d{7}$/,
  [CountryIds.Kuwait]: /^(((\+|00)?965)|0)?([569]\d{7}|41\d{6})$/,
};

/**
 * Custom signal-forms validation error emitted by {@link phoneNumberValidator}.
 *
 * Carries the value, the country id, and a human-readable description of the
 * expected number format so error messages can surface a precise hint.
 */
export type InvalidPhoneNumberError = ValidationError & {
  readonly kind: 'invalidPhoneNumber';
  readonly value: string;
  readonly countryId: number;
  readonly expectedFormat: string;
};

function getExpectedFormat(countryId: number): string {
  const formats: Record<number, string> = {
    [CountryIds.UAE]: 'UAE: 9 digits (e.g. 0501234567)',
    [CountryIds.KSA]: 'KSA: 9 digits (e.g. 0501234567)',
    [CountryIds.Qatar]: 'Qatar: 8 digits (e.g. 050123456)',
    [CountryIds.Oman]: 'Oman: 8 digits (e.g. 090123456)',
    [CountryIds.Bahrain]: 'Bahrain: 8 digits (e.g. 033123456)',
    [CountryIds.Kuwait]: 'Kuwait: 8 digits (e.g. 050123456)',
  };

  return formats[countryId] ?? 'Unknown format';
}

/**
 * Schema fragment that validates a phone number against the country selected
 * in a sibling `country_id` field.
 *
 * Applies a {@link validate} rule to `p.number` that reads `p.country_id`
 * reactively. The validation is skipped when the country has not yet been
 * picked, so the form does not error out while the user is still filling it.
 *
 * @param p An {@link ObjectSchemaPathTree} containing `number` and `country_id` fields.
 * @example
 * form(model, (p) => {
 *   required(p.number);
 *   phoneNumberValidator(p);
 * });
 */
export function phoneNumberValidator<
  T extends { number: string; country_id: number | null },
>(p: ObjectSchemaPathTree<T>): void {
  validate(p.number, (ctx) => {
    const countryId = ctx.valueOf(p.country_id);
    if (countryId === null || !isValidCountryId(countryId)) {
      return null;
    }

    const rawValue = ctx.value();
    const phoneNumber =
      typeof rawValue === 'string' ? rawValue.trim() : String(rawValue);
    const pattern = COUNTRIES_PHONE_PATTERNS[countryId];
    if (pattern.test(phoneNumber)) {
      return null;
    }

    const error: InvalidPhoneNumberError = {
      kind: 'invalidPhoneNumber',
      value: phoneNumber,
      countryId,
      expectedFormat: getExpectedFormat(countryId),
    };
    return error;
  });
}
